import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Map as MapIcon, Sparkles, Gauge } from 'lucide-react';
import PathwayRenderer from './components/PathwayRenderer';
import { LAYOUT_TYPES, layoutForSettings } from './components/layouts';
import ImpactPathHeader from './components/ImpactPathHeader';
import InfoDrawerTab from './components/InfoDrawerTab';
import SettingsDrawer from '../components/SettingsDrawer';
import CreateNodeModal from './components/CreateNodeModal';
import PainPointVotePanel from './components/PainPointVotePanel';
import BottomNotice from '../components/ui/BottomNotice';
import SimilarAfterAddModal, { SimilarAfterAddToast } from './components/SimilarAfterAdd';
import useSimilarAfterAdd from './hooks/useSimilarAfterAdd';
import { buildPathChain } from './utils/pathChain';
import { buildSeam, familyOf } from './utils/seam';
import socketService from '../../../../packages/shared/socket';
import ImpactPathNodeModal from './components/ImpactPathNodeModal';
import EffectTypePopover from './components/EffectTypePopover';
import { useImpactPathHandlers } from './hooks/useImpactPathHandlers';
import { useProposedNode } from './hooks/useProposedNode';
import { useInstance } from '../contexts/InstanceContext';
import { useInitData } from '../hooks/useInitData';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { detectChoicePoints } from '../config/valence';
import { withRootNode } from './utils/rootNode';
import { useImpactPathData } from '../screens/hooks/useImpactPathData';
import { normalizeEffectType } from '../config/effectTypes';
import { summariseEdges } from './utils/edgeSummary';
import { progressOnly, useProgressView } from './progressView';
import NodeNeighbourhood, { NEIGHBOURHOOD_MODAL_WIDTH, useWideEnough } from './neighbourhood/NodeNeighbourhood';
import MapDrawer from './components/MapDrawer';
import MapReadings from './components/MapReadings';
import ResetZoomButton from './components/ResetZoomButton';
import { useDrawer, TAB_BAR_WIDTH, DRAWER_WIDTH_RESERVE_MAX } from '../components/DrawerSystem';
import withSceneSwitch from './scene/withSceneSwitch'; // ?scene=1: the map drawn from buildScene
import SceneMap from './scene/SceneMap'; // settings.impactMapRenderer = 'scene': the scene as the map
import { Container, PreviewBadge, ErrorToast, BackToMap, MapRow, Canvas, CanvasNotice, ScenarioChoices, Notice } from './ImpactMapView.styles';
import SuggestionsPanel from './components/SuggestionsPanel';
import LoopDirectionPopover from './components/LoopDirectionPopover';
import { detectFeedbackLoops } from './components/feedbackLoops';
import { deriveLoopDirection, loopTone, LOOP_DIRECTIONS } from './components/loopVisuals';
import LinkKindPopover from './components/LinkKindPopover';
import ProposedLinkPopover from './components/ProposedLinkPopover';
import { getLastScenarioId, setLastScenarioId } from '../utils/lastScenarioStorage';
import API_ENDPOINTS from '../config/api';

/**
 * ImpactMapView — Impact Path v2.
 *
 * Replaces the pre-March screen of the same name, which rendered the scenario
 * and instance views before ImpactPathView absorbed them as type=2/type=3 and
 * which had been dead since the socket events it listened for were renamed
 * (`pathway-added` → `impact-path-node-added`). Nothing linked to it.
 *
 * Same data and same renderer as v1 (specs/impact-path-v2-prd.md §1): only the
 * shell is forked, so v2 can grow typed edges, blockers and loops without
 * touching the screen running live workshops. It wears the standard chrome —
 * ImpactPathHeader, the drawer tabs (Tietoa + Chat), SettingsDrawer — so it is
 * the same product, not a lab page.
 *
 * Reached two ways: `/impactmap` directly, or `/impactpath` when the
 * instance's chain uses `impact_path.v2` (ModuleRoute picks).
 */

// One shared empty list for the renderer's hidden layers. A fresh [] on every
// render is a new dependency for its layout effect, which then rebuilds every
// node element mid-click, and the click never lands.
const NONE = [];

// The loop as the room would read it aloud: A → B → C → A.
function loopRing(loop, pathways) {
  const byId = new Map(pathways.map((n) => [n.id, n.text || n.title || '']));
  const ids = loop.nodeIds?.length ? loop.nodeIds : [loop.closingToId, loop.closingFromId];
  const names = ids.map((id) => byId.get(id)).filter(Boolean);
  if (!names.length) return null;
  return [...names, names[0]].join(' → ');
}

// A loop turns towards what its nodes are: a loop of unwanted consequences is
// a vicious circle, of wanted ones a virtuous one. Mixed or unjudged stays
// unknown, for a person to decide (PRD §4d, loops as arcs).
function directionFromValence(loop, pathways) {
  if (!loop) return null;
  const byId = new Map(pathways.map((n) => [n.id, n.valence]));
  let wanted = 0; let unwanted = 0;
  (loop.nodeIds || []).forEach((id) => {
    const v = byId.get(id);
    if (v === 'wanted') wanted += 1; else if (v === 'unwanted') unwanted += 1;
  });
  const judged = wanted + unwanted;
  if (!judged) return null;
  if (unwanted / judged >= 0.7) return 'down';
  if (wanted / judged >= 0.7) return 'up';
  return null;
}

/** The first words of what was written, to stand on the card until the AI's title lands. */
const provisionalTitle = (text, max = 70) => {
  const one = String(text || '').replace(/\s+/g, ' ').trim();
  if (one.length <= max) return one;
  const cut = one.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 30))}…`;
};

export default withSceneSwitch(function ImpactMapView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId: contextInstanceId, currentInstance } = useInstance();
  const { t, language } = useTranslation();

  const kid = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId');
  const participantId = searchParams.get('participant');
  // lock=own once caged a participant who came in from /me in their own view
  // and hid the view switch, so they could never see the wall (21.9.2026).
  // /me no longer sends it; an old link with it still opens in Omat, but the
  // switch stays and Skenaario / Kaikki are one tap away.
  const lockOwn = false;
  const startOwn = searchParams.get('lock') === 'own';
  const instanceKey = kid || contextInstanceId;
  const urlScenarioId = searchParams.get('scenario');
  const scenarioId = urlScenarioId || getLastScenarioId(instanceKey);

  // v1 parity: type=1 individual, 2 scenario, 3 instance. Default to the whole
  // instance — v2 is a map of the system, not of one person's path.
  const viewType = searchParams.get('type');
  const viewMode = (lockOwn || (startOwn && !viewType)) ? 'individual' : viewType === '1' ? 'individual' : viewType === '2' ? 'scenario' : 'instance';

  const { shared } = useInitData(instanceKey);

  const [settingsVisible, setSettingsVisible] = useState(false);
  const [mapError, setMapError] = useState(null);
  // The room's vote on the pain-point groups. While it is open, a scenario
  // track shows the groups born under it instead of an empty map.
  const [voteOpen, setVoteOpen] = useState(false);
  useEffect(() => {
    if (!instanceKey) return undefined;
    let cancelled = false;
    const check = () => fetch(API_ENDPOINTS.PREWORK_VOTES(instanceKey), { credentials: 'include', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setVoteOpen(d.open === true); })
      .catch(() => {});
    check();
    const timer = setInterval(check, 20000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [instanceKey]);
  // Similar nodes found AFTER a node went on the map: offered, never imposed.
  const [findingsOpen, setFindingsOpen] = useState(false);
  // Nodes on the map whose title or similarity search is still brewing
  // (a tiny loader on the card), and the one just finished (a line at the bottom).
  const [brewingIds, setBrewingIds] = useState(() => new Set());
  const [justAdded, setJustAdded] = useState(null);
  useEffect(() => {
    if (!justAdded) return undefined;
    const timer = setTimeout(() => setJustAdded(null), 7000);
    return () => clearTimeout(timer);
  }, [justAdded]);

  // Adding consequences: its own switch, like every other layer in the bar.
  // This was a phase ("one question at a time") next to a row of layer
  // switches, and once the knots got their own switch the two collapsed into
  // each other — "Esteet" appeared twice in the same bar meaning two different
  // things. One vocabulary now.
  const [showConsequences, setShowConsequences] = useState(
    () => localStorage.getItem('kuutio-impactmap-consequences') !== 'off',
  );
  const changeShowConsequences = (next) => {
    localStorage.setItem('kuutio-impactmap-consequences', next ? 'on' : 'off');
    setShowConsequences(next);
  };

  // Ask the AI to check what gets added against the knowledge base. Off means
  // off: the claim is stored as written and no analysis is scheduled, so an
  // unchecked blocker is visibly unchecked rather than quietly unexamined.
  const [factCheck, setFactCheck] = useState(
    () => localStorage.getItem('kuutio-impactmap-factcheck') !== 'off',
  );
  const changeFactCheck = (next) => {
    localStorage.setItem('kuutio-impactmap-factcheck', next ? 'on' : 'off');
    setFactCheck(next);
  };

  // The knots on the connectors: the one way to add a blocker. A switch of its
  // own rather than a consequence of the mode, so a facilitator can leave them
  // out of the way while the room is still adding consequences.
  const [showBlockers, setShowBlockers] = useState(
    () => localStorage.getItem('kuutio-impactmap-blockers') !== 'off',
  );
  const changeShowBlockers = (next) => {
    localStorage.setItem('kuutio-impactmap-blockers', next ? 'on' : 'off');
    setShowBlockers(next);
  };

  // "What would this look like without the obstacles?" — the blockers come off
  // the map entirely and the paths are drawn as if nothing stopped them. The
  // data is untouched; this is a way of looking, not a way of editing.
  const [hideBlockers, setHideBlockers] = useState(false);
  const [progressView, toggleProgressView] = useProgressView(instanceKey); // Edistyminen: towns, milestones, goals

  // The other way of reading the same thing: keep the obstacles, drop what they
  // stop. What actually gets through.
  const [hideBlockedDownstream, setHideBlockedDownstream] = useState(false);

  // Redraw the map around one kind of relation. A way of looking, like the
  // blocker views — nothing is removed, only left out of this drawing.
  const [focus, setFocus] = useState(null);

  // One connector picked out of the crowd: it and the two cards it joins stay
  // lit, everything else recedes. A way of reading a busy map, not a mode —
  // panning still works and a tap on empty canvas puts it back.
  const [selection, setSelection] = useState(null);
  // Where the focused cards ended up, measured rather than guessed: the fit
  // decides the zoom, so the only way to sit under them is to look.
  const [backButtonTop, setBackButtonTop] = useState(null);

  // Cross-branch links: true, and also the fastest way to turn a readable tree
  // into a hairball. Off by default, per device.
  const [showGlobalLinks, setShowGlobalLinks] = useState(
    () => localStorage.getItem('kuutio-impactmap-global-links') === 'on',
  );
  const changeShowGlobalLinks = (next) => {
    localStorage.setItem('kuutio-impactmap-global-links', next ? 'on' : 'off');
    setShowGlobalLinks(next);
  };

  // Feedback loops: read-only for now — the detector reads the edges that are
  // already there (components/ImpactPath/feedbackLoops.js). Drawing loop-back
  // arrows is the next piece.
  const [showLoops, setShowLoops] = useState(
    () => localStorage.getItem('kuutio-impactmap-loops') === 'on',
  );
  const changeShowLoops = (next) => {
    localStorage.setItem('kuutio-impactmap-loops', next ? 'on' : 'off');
    setShowLoops(next);
  };

  // Valinnat — valence colouring and the forks it makes visible (§4b).
  const [showChoices, setShowChoices] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    if (!instanceKey) return;
    fetch(API_ENDPOINTS.INSTANCE_SCENARIOS(instanceKey))
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setScenarios(Array.isArray(data) ? data : (data.scenarios || [])))
      .catch(() => setScenarios([]));
  }, [instanceKey]);

  const {
    pathways, setPathways, connections, setConnections, blockers, setBlockers,
    loops: savedLoops, setLoops: setSavedLoops,
    blockerLinks, setBlockerLinks,
    pendingSuggestions, setPendingSuggestions,
    loading, scenarioInfo, participantInfo,
    loadPathways,
  } = useImpactPathData({
    kid,
    instanceId: contextInstanceId,
    scenarioId,
    participantId,
    viewMode,
    tulevaisuuskuva: null, includeProposed: true, // cards drafted from the team's documents, dashed until kept
  });

  // The forks the colouring makes visible: derived on every render from the
  // nodes themselves, because a choice point is not a stored object, it is what
  // the map currently says. Has to sit below `pathways` — a dependency array is
  // evaluated as an argument, so reading it any earlier throws on the binding
  // before it exists.
  const choicePoints = useMemo(
    () => detectChoicePoints(
      (pathways || []).map((n) => ({ id: n.id, parentId: n.parentId, valence: n.valence, isScenarioStarter: !!n.is_scenario_starter })),
    ),
    [pathways],
  );
  // The one card above the scenarios: the workshop and its main question.
  // Only the drawing sees it — loops, choices, analysis and counts keep
  // reading `pathways`, which is the map as stored.
  const drawnPathways = useMemo(
    () => withRootNode(progressView ? progressOnly(pathways) : pathways, {
      title: currentInstance?.title || '',
      question: currentInstance?.settings?.mainQuestion || '',
      goal: currentInstance?.settings?.workshopGoal || '',
    }),
    [pathways, progressView, currentInstance?.title, currentInstance?.settings?.mainQuestion, currentInstance?.settings?.workshopGoal],
  );
  const choicePointIds = useMemo(
    () => new Set(choicePoints.map((c) => c.nodeId)),
    [choicePoints],
  );
  // Nobody has judged anything yet → the switch says so rather than showing 0,
  // which would read as "this map contains no choices".
  const valenceJudged = useMemo(
    () => (pathways || []).some((n) => n.valence),
    [pathways],
  );

  useEffect(() => {
    if (scenarioId && instanceKey) setLastScenarioId(instanceKey, scenarioId);
  }, [scenarioId, instanceKey]);

  // Adding a consequence. v2 asks for the effect type up front; the rest of the
  // save (parent inheritance, depth, starter detection, POST) is v1's handler
  // untouched, so both screens write nodes the same way.
  const [createParent, setCreateParent] = useState(null);
  // Chosen in the popover before the create dialog opens. Null = the popover is
  // still open on the map.
  const [pendingEffectType, setPendingEffectType] = useState(null);
  // Screen coords of the (+) that was tapped — PathwayNodeLayer passes them as
  // the second argument, so the choice can open exactly where the click was.
  const [createAnchor, setCreateAnchor] = useState(null);
  // Writing a blocker (the create dialog, in red) and reading one.
  const [blockerDraftEdge, setBlockerDraftEdge] = useState(null);
  const [openBlocker, setOpenBlocker] = useState(null);

  const addBlocker = async (edge, text, description = null) => {
    const response = await fetch(API_ENDPOINTS.IMPACT_PATH_BLOCKERS(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        instanceId: instanceKey,
        scenarioId: scenarioId || null,
        parentNodeId: edge.from,
        childNodeId: edge.to,
        text,
        description,
        createdBy: participantId || null,
        factCheck,
      }),
    });
    if (!response.ok) {
      // Fail loudly: this failed silently once already, because a 404 from a
      // mis-mounted route looks exactly like nothing happening.
      const detail = await response.text().catch(() => '');
      console.error('❌ Failed to add blocker:', response.status, detail);
      setMapError(`Esteen lisäys epäonnistui (HTTP ${response.status}).`);
      return;
    }
    const { blocker } = await response.json();
    // The socket echo can beat this response back. Both append, so without the
    // guard the blocker you just wrote appears twice on your own screen.
    setBlockers((prev) => (prev.some((b) => b.id === blocker.id) ? prev : [...prev, blocker]));
    return blocker;
  };

  const removeBlocker = async (id) => {
    setBlockers((prev) => prev.filter((b) => b.id !== id));
    await fetch(API_ENDPOINTS.IMPACT_PATH_BLOCKER(id), { method: 'DELETE' })
      .catch((err) => console.error('Failed to remove blocker:', err));
  };

  // Retyping an existing edge: which one, and where its label was tapped.
  const [editEdge, setEditEdge] = useState(null);

  // Change the type of an edge that already exists. The type belongs to the
  // child row, so this is a node update, not an edge one.
  const retypeEdge = async (edge, type) => {
    const childId = edge?.to;
    if (!childId) return;
    setPathways((prev) => prev.map((n) => (n.id === childId ? { ...n, effectType: type } : n)));
    await fetch(API_ENDPOINTS.UPDATE_PATHWAY(childId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ effectType: type }),
    }).catch((err) => console.error('Failed to retype edge:', err));
  };
  // A person judging a node (§4b). Optimistic, like retypeEdge: the rail
  // changes under the finger, then the row is written. null clears it.
  const setNodeValence = async (nodeId, value) => {
    if (!nodeId) return;
    setPathways((prev) => prev.map((n) => (n.id === nodeId
      ? { ...n, valence: value, valenceSource: value ? 'human' : null }
      : n)));
    await fetch(API_ENDPOINTS.UPDATE_PATHWAY(nodeId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ valence: value }),
    }).catch((err) => console.error('Failed to set valence:', err));
  };
  const [selectedNode, setSelectedNode] = useState(null);
  const wideNeighbourhood = useWideEnough(); // the node modal's neighbourhood columns fit beside it
  const [modalVisible, setModalVisible] = useState(false);
  const proposalFor = useProposedNode({ instanceId: instanceKey, participantId, setPathways, onRejected: () => { setModalVisible(false); setSelectedNode(null); } });

  // Kartan pulssi (PRD §6b): the derived tier's summary — pivot candidates
  // and how many are new since the facilitator last ran the list. Seeded
  // from the instance, kept current by the tick event. A count, not a list:
  // the wall's list moves when the facilitator says.
  const [pulse, setPulse] = useState(() => currentInstance?.metadata?.pulse || null);
  useEffect(() => { if (currentInstance?.metadata?.pulse) setPulse(currentInstance.metadata.pulse); }, [currentInstance?.metadata?.pulse]);
  useEffect(() => {
    const sock = socketService.socket;
    if (!sock) return undefined;
    const onTick = (payload) => { if (payload?.instanceId === instanceKey && payload.pulse) setPulse(payload.pulse); };
    sock.on('map-pulse-tick', onTick);
    return () => sock.off('map-pulse-tick', onTick);
  }, [instanceKey]);

  // §4h — notes about the open node, and the participant's vote on it. Notes
  // are never structure: not clustered, typed or matched, only shown here
  // and counted on the card.
  const [nodeComments, setNodeComments] = useState([]);
  const [myVote, setMyVote] = useState(null);
  useEffect(() => {
    if (modalVisible && selectedNode?.id) { loadNodeComments(selectedNode.id); setMyVote(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisible, selectedNode?.id]);
  const loadNodeComments = async (nodeId) => {
    if (!nodeId) { setNodeComments([]); return; }
    try {
      const r = await fetch(API_ENDPOINTS.GET_NODE_COMMENTS(nodeId));
      const d = r.ok ? await r.json() : {};
      setNodeComments(d.comments || []);
    } catch { setNodeComments([]); }
  };
  const addNodeComment = async (nodeId, text) => {
    const r = await fetch(API_ENDPOINTS.ADD_NODE_COMMENT(nodeId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, instanceId: instanceKey, participantId: participantId || 'facilitator', participantName: participantInfo?.name || null }),
    });
    if (!r.ok) { setMapError('Huomion tallennus epäonnistui.'); return; }
    await loadNodeComments(nodeId);
    setPathways((prev) => prev.map((n) => (n.id === nodeId ? { ...n, commentCount: (n.commentCount || 0) + 1 } : n)));
  };
  const voteOnNode = async (nodeId, type) => {
    const r = await fetch(API_ENDPOINTS.VOTE_ON_NODE(nodeId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voteType: type, instanceId: instanceKey, participantId: participantId || 'facilitator' }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMapError(d.error === 'Cannot vote on your own submission' ? 'Omaa seurausta ei voi äänestää.' : 'Äänestys epäonnistui.'); return; }
    setMyVote((prev) => (prev === type ? null : type));
    if (d.votesUp !== undefined) {
      setPathways((prev) => prev.map((n) => (n.id === nodeId ? { ...n, votesUp: d.votesUp, votesDown: d.votesDown } : n)));
    }
  };
  // Analysoi (PRD §4c) — the one control that produces something.
  const { registerTab, unregisterTab, activeTab, drawerWidthPx } = useDrawer();
  const rendererRef = useRef(null);

  // The map's tools live in the global drawer as a tab beside info and chat
  // (PRD §4c). registerTab merges, so re-registering on state change is how
  // the tab's content stays current; only unmount unregisters.
  useEffect(() => () => { unregisterTab('impactmap'); unregisterTab('lukemat'); }, [unregisterTab]);
  // A part of the analysis names nodes; clicking it lights exactly those on
  // the map (everything else dims) and brings the first into view.
  const handleViewModeChange = (mode) => {
    const params = new URLSearchParams(searchParams);
    params.set('type', mode === 'individual' ? '1' : mode === 'scenario' ? '2' : '3');
    if ((mode === 'individual' || mode === 'scenario') && scenarioId) {
      params.set('scenario', scenarioId);
    }
    setSearchParams(params);
  };

  // The analysis reads the whole map, so a reference can point at a node in a
  // scenario this view is not showing. Then the view widens to Kaikki and the
  // focus waits for the reload; the layout needs a moment before it can zoom.
  const pendingFocusRef = useRef(null);
  const applyFocus = (list) => {
    setSelection({ ids: new Set(list), source: 'analysis' });
    setTimeout(() => rendererRef.current?.zoomToNode?.(list[0], 0.9, true), 350);
  };
  // ?focus=<id,id> — a link from elsewhere (the pivot card's chips) opens
  // the map on those nodes. Honoured once, after the first load.
  const focusParamDone = useRef(false);
  useEffect(() => {
    const raw = searchParams.get('focus');
    if (!raw || focusParamDone.current || !pathways.length) return;
    const ids = raw.split(',').filter((id) => pathways.some((n) => n.id === id));
    if (!ids.length) return;
    focusParamDone.current = true;
    setSelection({ ids: new Set(ids), source: 'analysis' });
    setTimeout(() => rendererRef.current?.zoomToNode?.(ids[0], 0.9, true), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathways]);

  const focusNodes = (ids) => {
    const wanted = (ids || []).filter(Boolean);
    if (!wanted.length) return;
    const here = wanted.filter((id) => pathways.some((n) => n.id === id));
    if (here.length) { applyFocus(here); return; }
    pendingFocusRef.current = wanted;
    if (viewMode !== 'instance' && !lockOwn) handleViewModeChange('instance');
  };
  useEffect(() => {
    const wanted = pendingFocusRef.current;
    if (!wanted) return;
    const here = wanted.filter((id) => pathways.some((n) => n.id === id));
    if (!here.length) return;
    pendingFocusRef.current = null;
    applyFocus(here);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathways]);

  const { handleSaveConsequence, handleNodeClick, handleNodeUpdate } = useImpactPathHandlers({
    factCheck,
    setSelectedNode,
    setModalVisible,
    setPathways,
    pathways,
    participantId,
    participantInfo,
    scenarioId,
    kid,
    instanceId: contextInstanceId,
    tulevaisuuskuva: null,
  });

  // Elastic push: while the type list is open at a (+), slide that node's whole
  // subtree right so the choice sits in the space the new node will occupy —
  // the map stretches to make room instead of the choice covering what is
  // already there. Transient: rendering only, the layout itself never changes.
  const CHOICE_GAP = 300;
  const subtreeOffsets = useMemo(() => {
    if (!createParent || pendingEffectType) return null;
    const childrenBy = {};
    pathways.forEach((n) => {
      const parent = n.parentId || n.parent_id;
      if (parent) (childrenBy[parent] = childrenBy[parent] || []).push(n.id);
    });
    const offsets = {};
    const stack = [...(childrenBy[createParent.id] || [])];
    while (stack.length) {
      const id = stack.pop();
      if (offsets[id]) continue;
      offsets[id] = CHOICE_GAP;
      (childrenBy[id] || []).forEach((child) => stack.push(child));
    }
    return Object.keys(offsets).length ? offsets : null;
  }, [createParent, pendingEffectType, pathways]);

  const detectedLoops = useMemo(
    () => (showLoops ? detectFeedbackLoops(pathways, connections, blockerLinks, blockers) : []),
    [showLoops, pathways, connections, blockerLinks, blockers],
  );

  // A link dragged from one card to another. Answered in two steps: what kind
  // of link it is, then how it acts — both in place at the drop point.
  // { fromId, toId, anchor, kind?, saving? }
  const [linkDraft, setLinkDraft] = useState(null);
  // A loop that has been opened for reading or naming.
  const [openLoop, setOpenLoop] = useState(null);
  const [rootOpen, setRootOpen] = useState(false);
  // Asking which way a new loop runs, before anything is written.
  const [loopDirectionDraft, setLoopDirectionDraft] = useState(null);

  // Retyping a loop's closing link, from the pill on its arc.
  // Same, for a cross-branch link.
  const [crossTypeEdit, setCrossTypeEdit] = useState(null);
  // A proposed link (metadata.status 'proposed', e.g. a town goal → shared target the
  // target round drew) tapped on the scene: { link: { from, to, effectType, reason }, anchor }.
  const [proposedLinkEdit, setProposedLinkEdit] = useState(null);
  const decideProposedLink = async (link, decision) => {
    setProposedLinkEdit(null);
    const response = await fetch(API_ENDPOINTS.DECIDE_PROPOSED_CONNECTION(), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instanceId: instanceKey, parentNodeId: link.from, childNodeId: link.to, decision, participantId: participantId || 'facilitator' }),
    });
    if (!response.ok) { setMapError(t('impactPath.proposedLink.failed', { status: response.status })); return; }
    // A rejected link leaves the map; an accepted one stays. Either way the rows changed,
    // so the map graph is fetched again and the scene redraws.
    setConnections((prev) => (decision === 'reject'
      ? prev.filter((c) => !((c.parentNodeId || c.parent_node_id || c.from) === link.from && (c.childNodeId || c.child_node_id || c.to) === link.to))
      : [...prev]));
  };

  // Two homes, one per shape: node→node is a v1 multi-parent connection, and a
  // link touching a blocker goes to the v2 links table (pathway_connections
  // requires both ends to be submissions).
  const createLink = async (draft, type) => {
    const touchesBlocker = draft.fromKind === 'blocker' || draft.toKind === 'blocker';

    const response = touchesBlocker
      ? await fetch(API_ENDPOINTS.IMPACT_PATH_LINKS(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instanceId: instanceKey,
          scenarioId: scenarioId || null,
          fromKind: draft.fromKind,
          fromId: draft.fromId,
          toKind: draft.toKind,
          toId: draft.toId,
          effectType: type,
          createdBy: participantId || 'facilitator',
        }),
      })
      : await fetch(API_ENDPOINTS.CREATE_PATHWAY_CONNECTION(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parentNodeId: draft.fromId,
          childNodeId: draft.toId,
          // Required by the route. A facilitator has no participant id, and a
          // missing one used to come back as a bare 400 with nothing on screen.
          createdBy: participantId || 'facilitator',
          scenarioId: scenarioId || null,
          connectionType: type,
        }),
      });

    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      console.error('❌ Failed to create link:', response.status, detail);
      setMapError(response.status === 409
        ? 'Näiden välillä on jo yhteys.'
        : `Yhteyden luonti epäonnistui (HTTP ${response.status}). ${detail.error || ''}`.trim());
      return null;
    }

    if (touchesBlocker) {
      const { link } = await response.json();
      setBlockerLinks((prev) => (prev.some((l) => l.id === link.id) ? prev : [...prev, link]));
      return { blockerLink: link };
    }
    const link = { parentId: draft.fromId, childId: draft.toId, type };
    setConnections((prev) => [...prev, link]);
    return link;
  };

  /**
   * Would this link close a ring? Asked before anything is written, so a link
   * drawn as a loop that turns out not to be one leaves nothing behind. The
   * candidate edge is handed to the detector rather than saved first: a
   * half-finished loop that quietly persists as an ordinary connection is the
   * kind of thing nobody goes looking for later.
   */
  const loopClosedBy = (draft, type) => {
    const touchesBlocker = draft.fromKind === 'blocker' || draft.toKind === 'blocker';
    const candidateConnections = touchesBlocker
      ? connections
      : [...connections, { parentId: draft.fromId, childId: draft.toId, type }];
    const candidateLinks = touchesBlocker
      ? [...blockerLinks, { fromId: draft.fromId, toId: draft.toId, effectType: type }]
      : blockerLinks;
    return detectFeedbackLoops(pathways, candidateConnections, candidateLinks, blockers)
      .find((l) => l.edges.some((e) => e.from === draft.fromId && e.to === draft.toId)) || null;
  };

  const finishLink = async (type) => {
    const draft = linkDraft;
    setLinkDraft(null);
    if (!draft) return;

    // "On sama" is the whole claim; there is no polarity to pick.
    if (draft.kind === 'same') {
      await createLink(draft, 'is_same');
      return;
    }

    if (draft.kind === 'loop') {
      const closed = loopClosedBy(draft, type);
      if (!closed) {
        setMapError('Tämä ei sulje silmukkaa — ketju ei palaa tähän solmuun, joten yhteyttä ei tallennettu. Piirrä uudelleen ja valitse "Yhteys", jos tarkoitit tavallista yhteyttä.');
        return;
      }
      // Which way the spiral runs is asked here, while the person still has the
      // whole chain in their head. Nothing is written until they answer.
      if (closed.kind === 'balancing') {
        const link = await createLink(draft, type);
        if (link) await createLoop(closed, draft);
        return;
      }
      setLoopDirectionDraft({ draft, type, closed, anchor: draft.anchor });
      return;
    }

    await createLink(draft, type);
  };

  // Created the moment it is drawn, with the name the map can work out on its
  // own. Being asked to name a spiral in the same breath as drawing it
  // interrupts the thought that produced it — the words come later, from the
  // badge.
  const createLoop = async (closed, draft, suggestedTitle = null, suggestedDirection = null, suggestedDescription = null) => {
    const chain = closed.nodeTexts.filter(Boolean);
    const generatedTitle = suggestedTitle || (chain.length
      ? `${chain.slice(0, 3).join(' → ')}${chain.length > 3 ? ' → …' : ''}`
      : (closed.kind === 'balancing' ? 'Tasapainottava silmukka' : 'Vahvistava silmukka'));

    const response = await fetch(API_ENDPOINTS.IMPACT_PATH_LOOPS(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        instanceId: instanceKey,
        scenarioId: scenarioId || null,
        title: generatedTitle,
        // The AI's one or two sentences on why the ring holds, when it
        // proposed the loop. A person rewrites them from the badge.
        description: suggestedDescription,
        kind: closed.kind,
        direction: suggestedDirection || directionFromValence(closed, pathways) || deriveLoopDirection(closed),
        hasDelay: closed.hasDelay,
        nodeIds: closed.nodeIds,
        closingFromId: draft.fromId,
        closingToId: draft.toId,
        createdBy: participantId || null,
      }),
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      console.error('❌ Failed to save loop:', response.status, detail);
      setMapError(`Silmukan tallennus epäonnistui (HTTP ${response.status}).`);
      return;
    }
    const { loop } = await response.json();
    setSavedLoops((prev) => (prev.some((l) => l.id === loop.id) ? prev : [...prev, loop]));
  };

  const retypeCrossLink = async (link, type) => {
    const response = await fetch(API_ENDPOINTS.CREATE_PATHWAY_CONNECTION(), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentNodeId: link.from, childNodeId: link.to, connectionType: type }),
    });
    if (!response.ok) {
      console.error('❌ Failed to retype cross link:', response.status);
      setMapError(`Yhteyden tyypin vaihto epäonnistui (HTTP ${response.status}).`);
      return;
    }
    setConnections((prev) => prev.map((c) => (
      (c.parentId || c.parent_node_id) === link.from && (c.childId || c.child_node_id) === link.to
        ? { ...c, type }
        : c
    )));
  };

  const setLoopDirection = async (loop, direction) => {
    setSavedLoops((prev) => prev.map((l) => (l.id === loop.id ? { ...l, direction } : l)));
    setOpenLoop((prev) => (prev ? { ...prev, direction } : prev));
    const response = await fetch(API_ENDPOINTS.IMPACT_PATH_LOOP(loop.id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ direction }),
    });
    if (!response.ok) setMapError(`Silmukan suunnan tallennus epäonnistui (HTTP ${response.status}).`);
  };

  const renameLoop = async (id, title, description) => {
    setSavedLoops((prev) => prev.map((l) => (l.id === id ? { ...l, title, description } : l)));
    const response = await fetch(API_ENDPOINTS.IMPACT_PATH_LOOP(id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, description }),
    });
    if (!response.ok) {
      console.error('❌ Failed to rename loop:', response.status);
      setMapError(`Silmukan tallennus epäonnistui (HTTP ${response.status}).`);
    }
  };

  const removeLoop = async (id) => {
    setSavedLoops((prev) => prev.filter((l) => l.id !== id));
    await fetch(API_ENDPOINTS.IMPACT_PATH_LOOP(id), { method: 'DELETE' })
      .catch((err) => console.error('Failed to remove loop:', err));
  };

  // AI proposals. They stay proposals until somebody says yes — a loop and a
  // cross-branch link are the strongest claims the map can carry.
  const [suggestBusy, setSuggestBusy] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  // Re-picking a proposal's type before accepting it.
  const [suggestionTypeEdit, setSuggestionTypeEdit] = useState(null);

  const runSuggest = async (kind) => {
    setSuggestBusy(kind);
    setSuggestions(null);
    try {
      const response = await fetch(API_ENDPOINTS.IMPACT_PATH_SUGGEST(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId: instanceKey, scenarioId: scenarioId || null, kind }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        setMapError(`Ehdotusten haku epäonnistui (HTTP ${response.status}). ${detail.error || ''}`.trim());
        return;
      }
      const data = await response.json();
      const items = data.suggestions || [];
      setSuggestions({ kind, items, error: data.error || null });
      // The queue the map counts is everything still undecided, whichever
      // search produced it.
      setPendingSuggestions((prev) => [
        ...prev.filter((p) => p.kind !== kind),
        ...items,
      ]);
    } catch (err) {
      console.error('❌ Suggestion request failed:', err);
      setMapError('Ehdotusten haku epäonnistui.');
    } finally {
      setSuggestBusy(null);
    }
  };

  // A decision, not a dismissal from view: rejected proposals stay on record so
  // the next search does not offer the same thing again.
  const decideSuggestion = async (s, status) => {
    setPendingSuggestions((prev) => prev.filter((p) => p.id !== s.id));
    setSuggestions((prev) => (prev
      ? { ...prev, items: prev.items.filter((i) => i.id !== s.id) }
      : prev));
    if (!s.id) return;
    await fetch(API_ENDPOINTS.IMPACT_PATH_SUGGESTION(s.id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, decidedBy: participantId || 'facilitator' }),
    }).catch((err) => console.error('Failed to record suggestion decision:', err));
  };

  /**
   * The nodes a proposed loop would run through: up the primary-parent tree
   * from the far end back to the ancestor it closes onto. That chain is what
   * makes it a loop rather than a pair, and it is the part the panel had no way
   * to show.
   */
  const suggestionChain = (s) => {
    const byId = new Map(pathways.map((n) => [n.id, n]));
    const chain = [];
    let cur = byId.get(s.fromId);
    let guard = 0;
    while (cur && guard < 40) {
      chain.unshift({ id: cur.id, text: cur.title || cur.text || '', valence: cur.valence || null });
      if (cur.id === s.toId) break;
      const parentId = cur.parentId || cur.parent_id;
      cur = parentId ? byId.get(parentId) : null;
      guard += 1;
    }
    if (!chain.length || chain[0].id !== s.toId) return [];
    // A long chain is a story nobody reads in a list: keep both ends and the
    // step either side of the gap.
    if (chain.length > 5) {
      return [chain[0], chain[1], { gap: true }, chain[chain.length - 2], chain[chain.length - 1]];
    }
    return chain;
  };

  const acceptSuggestion = async (s) => {
    const draft = { fromId: s.fromId, fromKind: 'node', toId: s.toId, toKind: 'node' };
    const link = await createLink(draft, s.effectType);
    if (!link) return;
    if (suggestions?.kind === 'loops') {
      const closed = detectFeedbackLoops(pathways, [...connections, link], blockerLinks, blockers)
        .find((l) => l.edges.some((e) => e.from === s.fromId && e.to === s.toId));
      // The detector is the authority, not the proposal: an edge the AI called
      // a loop that does not close one is a cross-link, and is left as that.
      if (closed) await createLoop(closed, draft, s.title, s.direction || null, s.reasoning || null);
    }
    await decideSuggestion(s, 'accepted');
  };

  // Which cards an undecided proposal touches, so the queue is visible where
  // the work is and not only as a number in the bar.
  const suggestedNodeIds = useMemo(() => {
    const ids = new Set();
    pendingSuggestions.forEach((s) => { ids.add(s.fromId); ids.add(s.toId); });
    return ids;
  }, [pendingSuggestions]);

  useEffect(() => {
    if (!selection) { setBackButtonTop(null); return undefined; }
    const place = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const cards = [...canvas.querySelectorAll('.node')];
      if (!cards.length) return;
      const canvasTop = canvas.getBoundingClientRect().top;
      const bottom = Math.max(...cards.map((c) => c.getBoundingClientRect().bottom));
      setBackButtonTop(Math.round(bottom - canvasTop + 22));
    };
    // After the fit transition has landed, and again if the window changes.
    const timer = setTimeout(place, 420);
    window.addEventListener('resize', place);
    return () => { clearTimeout(timer); window.removeEventListener('resize', place); };
  }, [selection]);

  const { findings, findSimilar, clearFindings } = useSimilarAfterAdd({
    pathways, scenarioId, instanceId: instanceKey, scenarioInfo, language,
  });

  const needsScenario = (viewMode === 'individual' || viewMode === 'scenario') && !scenarioId;

  const chooseScenario = (id) => {
    const params = new URLSearchParams(searchParams);
    params.set('scenario', id);
    setSearchParams(params);
  };

  const canvasRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // The scene as the map (specs/tki-kuutio-flow.md → Renderer), per instance. It draws
  // the map graph, so after any change to the rows it is fetched again and re-derived.
  const sceneMap = currentInstance?.settings?.impactMapRenderer === 'scene';
  const [graphKey, setGraphKey] = useState(0);
  useEffect(() => { if (sceneMap) setGraphKey((k) => k + 1); }, [sceneMap, pathways, connections, blockers, savedLoops]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [instanceKey]);

  const edgeSummary = useMemo(() => summariseEdges(pathways, connections), [pathways, connections]);

  // Types every untyped edge from its chain (edgeTypeService). A person's
  // type is kept; the drawer counts the two apart.
  const [typingEdges, setTypingEdges] = useState(false);
  const runEdgeTypes = async () => {
    setTypingEdges(true);
    try {
      const response = await fetch(API_ENDPOINTS.IMPACT_PATH_EDGE_TYPES(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId: instanceKey }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        setMapError(`Kytkentöjen tyypitys epäonnistui (HTTP ${response.status}). ${detail.error || ''}`.trim());
        return;
      }
      await loadPathways();
    } finally {
      setTypingEdges(false);
    }
  };

  // The drawer reads live state and calls back into this view. The props are
  // held in a ref so the tab's render always sees the current ones, and the
  // tab is re-registered only when a value the drawer shows changes. Without
  // a dependency list this ran after every commit, registerTab's setTabs
  // re-rendered the view, and the loop swallowed every node click.
  const drawerProps = {
    instanceId: instanceKey,
    onFocusNodes: focusNodes,
    typedEdges: edgeSummary.typed,
    aiTypedEdges: edgeSummary.aiTyped,
    onTypeEdges: runEdgeTypes,
    typingEdges,
    totalEdges: edgeSummary.total,
    showChoices,
    onShowChoicesChange: setShowChoices,
    choiceCount: choicePoints.length,
    valenceJudged,
    factCheck,
    onFactCheckChange: changeFactCheck,
    showLoops,
    onShowLoopsChange: changeShowLoops,
    loopCount: detectedLoops.length,
    showGlobalLinks,
    onShowGlobalLinksChange: changeShowGlobalLinks,
    showBlockers,
    onShowBlockersChange: changeShowBlockers,
    hideBlockers,
    onHideBlockersChange: setHideBlockers,
    hideBlockedDownstream,
    onHideBlockedDownstreamChange: setHideBlockedDownstream,
    showConsequences,
    onShowConsequencesChange: changeShowConsequences,
    crossLinkCount: connections.length,
    onFindLoops: () => runSuggest('loops'),
    onFindCrossLinks: () => runSuggest('cross'),
    pendingLoops: pendingSuggestions.filter((p) => p.kind === 'loops'),
    pendingCross: pendingSuggestions.filter((p) => p.kind === 'cross'),
    focus,
    onFocusChange: setFocus,
    onOpenPending: (kind) => setSuggestions({
      kind,
      items: pendingSuggestions.filter((p) => p.kind === kind),
      error: null,
    }),
    busy: suggestBusy,
    blockerCount: blockers.length,
    pulse,
    progressView,
    onProgressViewToggle: toggleProgressView,
  };
  const drawerPropsRef = useRef(drawerProps);
  drawerPropsRef.current = drawerProps;
  useEffect(() => {
    if (needsScenario) return;
    registerTab({
      id: 'impactmap',
      label: 'Kartta',
      icon: <Sparkles size={18} />,
      color: 'rgba(124, 58, 237, 0.3)',
      borderColor: 'rgba(124, 58, 237, 0.5)',
      order: 5,
      render: () => <MapDrawer {...drawerPropsRef.current} />,
    });
    // Lukemat — the map's own data, asked new questions (MapReadings). Its
    // rows light nodes on the map through the same focus the drawer uses.
    registerTab({
      id: 'lukemat',
      label: 'Lukemat',
      icon: <Gauge size={18} />,
      color: 'rgba(240, 179, 94, 0.25)',
      borderColor: 'rgba(240, 179, 94, 0.5)',
      order: 6,
      render: () => <MapReadings instanceId={instanceKey} onFocusNodes={drawerPropsRef.current.onFocusNodes} />,
    });
  }, [needsScenario, registerTab, instanceKey, edgeSummary, typingEdges, showChoices, choicePoints.length,
    valenceJudged, factCheck, showLoops, detectedLoops.length, showGlobalLinks, showBlockers,
    hideBlockers, hideBlockedDownstream, showConsequences, connections.length, pendingSuggestions,
    focus, suggestBusy, blockers.length, pulse, progressView]);

  if (!instanceKey) {
    return (
      <Container>
        <Notice>
          <MapIcon className="icon" />
          <h2>Ei instanssia</h2>
          <p>Avaa kartta instanssin kanssa: <code>/impactmap?kid=…</code></p>
        </Notice>
      </Container>
    );
  }

  const instanceTitle = currentInstance?.title || '';

  return (
    <Container>
      <ImpactPathHeader
        inDocumentFlow
        currentViewMode={viewMode}
        lockView={lockOwn}
        scenarioId={scenarioId || ''}
        scenarioInfo={scenarioInfo}
        instanceTitle={instanceTitle}
        onViewModeChange={lockOwn ? () => {} : handleViewModeChange}
        onChangeScenario={() => {
          const params = new URLSearchParams(searchParams);
          params.delete('scenario');
          params.delete('type');
          setSearchParams(params);
        }}
        onSettingsClick={() => setSettingsVisible(true)}
        subtitleExtra={<PreviewBadge>v2</PreviewBadge>}
        showScenarioSwitch={false}
        scenarios={scenarios}
        onSelectScenario={chooseScenario}
      />

      <MapRow $drawerOpen={!!activeTab} $drawerWidth={drawerWidthPx}>
      <Canvas ref={canvasRef}>
        {mapError && (
          <ErrorToast>
            <span>{mapError}</span>
            <button type="button" onClick={() => setMapError(null)} aria-label="Sulje">×</button>
          </ErrorToast>
        )}
        {!findingsOpen && (
          <SimilarAfterAddToast findings={findings} onShow={() => setFindingsOpen(true)} onDismiss={clearFindings} />
        )}
        {!findings && justAdded && (
          <BottomNotice tone="success" message={t('impactPath.addNode.added', { title: justAdded.title })}
                        onDismiss={() => setJustAdded(null)} />
        )}
        {/* Omat/Skenaario need a scenario; without one v1 shows a selector and
            v2 used to show nothing at all — an empty black canvas below a
            working header, which reads as a broken page rather than a choice
            waiting to be made. */}
        {needsScenario && (
          <CanvasNotice>
            <h2>Valitse skenaario</h2>
            <p>
              {viewMode === 'individual'
                ? 'Omat-näkymä näyttää yhden skenaarion polkusi.'
                : 'Skenaario-näkymä näyttää yhden skenaarion kaikki polut.'}
              {' '}Valitse skenaario tai katso koko kartta Kaikki-näkymässä.
            </p>
            <ScenarioChoices>
              {scenarios.map((sc) => (
                <button key={sc.id} type="button" onClick={() => chooseScenario(sc.id)}>
                  {sc.icon ? `${sc.icon} ` : ''}{sc.title}
                </button>
              ))}
              <button type="button" onClick={() => handleViewModeChange('instance')}>
                Kaikki →
              </button>
            </ScenarioChoices>
          </CanvasNotice>
        )}

        {!needsScenario && loading && (
          <CanvasNotice><p>Ladataan karttaa…</p></CanvasNotice>
        )}

        {!needsScenario && voteOpen && scenarioId && (
          <PainPointVotePanel
            instanceId={instanceKey}
            scenarioId={scenarioId}
            scenarioTitle={scenarioInfo?.title}
            participantId={participantId}
            onFinished={async () => { setVoteOpen(false); await loadPathways(); }}
          />
        )}

        {!needsScenario && !loading && pathways.length === 0 && !(voteOpen && scenarioId) && (
          <CanvasNotice>
            <h2>Kartta on tyhjä</h2>
            <p>
              Tässä näkymässä ei ole vielä solmuja. Lisää ensimmäinen seuraus
              vaikutuspolussa tai vaihda näkymää.
            </p>
          </CanvasNotice>
        )}

        {!needsScenario && !loading && pathways.length > 0 && size.width > 0 && sceneMap && (
          <SceneMap
            ref={rendererRef}
            instanceId={instanceKey}
            refreshKey={graphKey}
            pathways={pathways}
            blockers={blockers}
            loops={savedLoops}
            width={size.width}
            height={size.height}
            onNodeClick={handleNodeClick}
            onRootClick={() => setRootOpen(true)}
            onBlockerClick={setOpenBlocker}
            onLoopClick={setOpenLoop}
            selectionIds={selection?.ids || null}
            showAddButtons={showConsequences}
            showKnots={showBlockers && !hideBlockers}
            showLinkHandles={showLoops || showGlobalLinks}
            onAddConsequenceClick={!showConsequences ? null : (node, screenPosition) => { setCreateParent(node); setCreateAnchor(screenPosition || null); }}
            onEdgeKnotClick={(!showBlockers || hideBlockers) ? null : (edge, screenPosition) => setBlockerDraftEdge({ edge, anchor: screenPosition })}
            onEdgeLabelClick={showConsequences ? (edge, screenPosition) => setEditEdge({ edge, anchor: screenPosition }) : null}
            onCrossLinkTypeClick={(link, anchor) => setCrossTypeEdit({ link, anchor })}
            onProposedLinkClick={(link, anchor) => setProposedLinkEdit({ link, anchor })}
            scenarioId={viewMode === 'instance' ? null : scenarioId}
            focus={focus}
            focusChain={selection?.chain || (selection?.from && selection?.to ? [selection.from, selection.to] : null)}
            progressView={progressView}
            hideBlockedDownstream={hideBlockedDownstream}
            showGlobalLinks={showGlobalLinks || layoutForSettings(currentInstance?.settings) === LAYOUT_TYPES.ROUTE}
            showChoices={showChoices}
            choicePointIds={choicePointIds}
            onDrawLink={(from, to, anchor) => {
              if (from.kind === 'blocker') { createLink({ fromId: from.id, fromKind: 'blocker', toId: to.id, toKind: to.kind }, 'blocks'); return; }
              setLinkDraft({ fromId: from.id, fromKind: from.kind, toId: to.id, toKind: to.kind, anchor });
            }}
          />
        )}
        {!needsScenario && !loading && pathways.length > 0 && size.width > 0 && !sceneMap && (
          <PathwayRenderer
            ref={rendererRef}
            showChoices={showChoices}
            choicePointIds={choicePointIds}
            choicePoints={choicePoints}
            pathways={drawnPathways}
            connections={progressView ? NONE : connections}
            glideKey={progressView ? 'progress' : 'full'}
            scenarios={scenarios}
            // A tap on a seam mark lights the family (§4j): the merged root
            // and its subtree, every member and its chain to its muuttuja.
            // The same selection the analysis chips use, so Takaisin karttaan
            // clears it. The seam is rebuilt here from the same inputs the
            // renderer used, so the ids match what is drawn.
            onSeamTap={(rootId) => {
              const seam = buildSeam(drawnPathways, connections, { scenarios });
              const lit = familyOf(rootId, seam.pathways, seam.roots);
              if (lit) setSelection({ ids: lit, source: 'seam', color: '#f0b35e' });
            }}
            width={size.width}
            height={size.height}
            layoutType={layoutForSettings(currentInstance?.settings)}
            currentParticipantId={participantId}
            processingNodeIds={brewingIds}
            showEdgeTypes
            colorEdgesByType={false}
            nodeOffsets={subtreeOffsets}
            animatePositions
            onEdgeLabelClick={showConsequences ? (edge, screenPosition) => setEditEdge({ edge, anchor: screenPosition }) : null}
            onNodeClick={(node) => (node?.isRoot ? setRootOpen(true) : handleNodeClick(node))}
            hideBlockedDownstream={hideBlockedDownstream}
            focus={focus}
            suggestedNodeIds={suggestedNodeIds}
            selection={selection}
            onSelectEdge={setSelection}
            focusChain={selection?.chain || (selection?.from && selection?.to
              ? [selection.from, selection.to] : null)}
            focusLabel={selection?.label || null}
            focusColor={selection?.color || null}
            focusLoop={selection?.loop || null}
            blockers={hideBlockers || progressView ? NONE : blockers}
            blockerLinks={hideBlockers || progressView ? NONE : blockerLinks}
            showAddButtons={showConsequences}
            showLinkHandles={showLoops || showGlobalLinks}
            showGlobalLinks={showGlobalLinks || layoutForSettings(currentInstance?.settings) === LAYOUT_TYPES.ROUTE}
            onCrossLinkTypeClick={(link, anchor) => setCrossTypeEdit({ link, anchor })}
            loops={showLoops && !progressView ? savedLoops : NONE}
            onLoopClick={setOpenLoop}
            onDrawLink={(from, to, anchor) => {
              // A blocker has one thing to say about anything it reaches, so it
              // is not asked. Straight to the link, typed 'blocks'.
              if (from.kind === 'blocker') {
                createLink({ fromId: from.id, fromKind: 'blocker', toId: to.id, toKind: to.kind }, 'blocks');
                return;
              }
              setLinkDraft({
                fromId: from.id, fromKind: from.kind, toId: to.id, toKind: to.kind, anchor,
              });
            }}
            // The knot on a clear edge and the (+) under a stack mean the same
            // thing: add one here. Opening an existing blocker is a click on
            // that card, so there is no list to go through.
            onEdgeKnotClick={(!showBlockers || hideBlockers) ? null : (edge, screenPosition) => {
              setBlockerDraftEdge({ edge, anchor: screenPosition });
            }}
            onBlockerClick={(blocker) => setOpenBlocker(blocker)}
            onAddConsequenceClick={!showConsequences ? null : (node, screenPosition) => {
              setCreateParent(node);
              setCreateAnchor(screenPosition || null);
            }}
            // Deliberately not highlighting the parent while the list is open:
            // the highlight expands the card to its full description, which
            // pulls attention away from the choice being made.
            highlightedNodeId={null}
          />
        )}
        {selection && (
          <BackToMap
            type="button"
            style={{ top: backButtonTop ?? 20, opacity: backButtonTop == null ? 0 : 1 }}
            onClick={() => setSelection(null)}
          >
            ← Takaisin karttaan
          </BackToMap>
        )}

        {suggestions && (
          <SuggestionsPanel
            kind={suggestions.kind}
            suggestions={suggestions.items}
            error={suggestions.error}
            onAccept={acceptSuggestion}
            onDismiss={(s) => decideSuggestion(s, 'dismissed')}
            onClose={() => setSuggestions(null)}
            onTypeClick={(s, anchor) => setSuggestionTypeEdit({ suggestion: s, anchor })}
            chainOf={suggestionChain}
          />
        )}
        {!needsScenario && !loading && pathways.length > 0 && <ResetZoomButton onClick={() => rendererRef.current?.fitNow?.(true)} />}
      </Canvas>
      </MapRow>

      {/* Side tabs: Tietoa here, Chat registered app-wide in App.jsx */}
      <InfoDrawerTab
        instanceId={instanceKey}
        instanceTitle={instanceTitle}
        instanceDescription={currentInstance?.description || ''}
        scenarios={shared?.scenarios || {}}
        scenarioInfo={scenarioInfo}
        currentViewMode={viewMode}
        onViewModeChange={lockOwn ? () => {} : handleViewModeChange}
        participantCount={currentInstance?.participant_count}
      />

      {/* Writing a blocker uses the same dialog as writing a consequence, in
          red — one way of adding things to the map, not two. It shows the
          consequence the blocker stops (the edge's child, not its parent); on an
          unwanted one the question turns: how is this avoided? */}
      {blockerDraftEdge && (() => {
        const blocked = pathways.find((n) => n.id === blockerDraftEdge.edge.to) || null;
        const k = blocked?.valence === 'unwanted' ? 'avoid' : 'block';
        return (
        <CreateNodeModal
          visible
          variant="blocker"
          parentNode={blocked}
          parentLabel={t(`impactPath.blockerDraft.${k}.label`)}
          title={t(`impactPath.blockerDraft.${k}.title`)}
          subtitle={t(`impactPath.blockerDraft.${k}.subtitle`)}
          saveLabel={t(`impactPath.blockerDraft.${k}.title`)}
          onSave={async (data) => {
            const edge = blockerDraftEdge.edge;
            setBlockerDraftEdge(null);
            await addBlocker(edge, data.text, data.description);
          }}
          onClose={() => setBlockerDraftEdge(null)}
        />
        );
      })()}

      {/* A blocker reads like a node: same modal, same evaluation panel. */}
      {openBlocker && (
        <ImpactPathNodeModal
          instanceId={instanceKey}
          isOpen
          mode="view"
          position="center"
          node={{
            id: openBlocker.id,
            text: openBlocker.text,
            title: openBlocker.text,
            fullText: openBlocker.description || openBlocker.text,
            description: openBlocker.description,
            node_analysis: openBlocker.analysis || null,
            references: openBlocker.analysis?.references || [],
            is_scenario_starter: false,
          }}
          entityBadge={{ text: 'Este', color: '#EF5350' }}
          isCreator
          isAdmin={false}
          onClose={() => setOpenBlocker(null)}
          onDelete={async (id) => { await removeBlocker(id); setOpenBlocker(null); }}
        />
      )}

      {/* Two questions at the drop point: what kind of link, then how it acts. */}
      {linkDraft && !linkDraft.kind && (
        <LinkKindPopover
          anchor={linkDraft.anchor}
          onSelect={(kind) => {
            // "On sama" is the whole claim; there is no polarity to pick.
            if (kind === 'same') {
              const draft = linkDraft;
              setLinkDraft(null);
              createLink({ ...draft, kind }, 'is_same');
              return;
            }
            setLinkDraft((d) => ({ ...d, kind }));
          }}
          onClose={() => setLinkDraft(null)}
        />
      )}

      {linkDraft?.kind && linkDraft.kind !== 'same' && (
        <EffectTypePopover
          anchor={linkDraft.anchor}
          onSelect={finishLink}
          onClose={() => setLinkDraft(null)}
        />
      )}

      {/* The workshop root: the full goal as the card's description, read-only —
          it is set in the instance settings, not on the map. */}
      {rootOpen && (
        <ImpactPathNodeModal
          instanceId={instanceKey}
          isOpen
          mode="view"
          position="center"
          node={{
            id: 'map-root',
            text: currentInstance?.settings?.mainQuestion || currentInstance?.settings?.workshopGoal || '',
            title: currentInstance?.settings?.mainQuestion || currentInstance?.settings?.workshopGoal || '',
            fullText: currentInstance?.settings?.workshopGoal || '',
            description: currentInstance?.settings?.workshopGoal || '',
            is_scenario_starter: false,
          }}
          entityBadge={{ text: currentInstance?.title || '', color: '#4CAF50' }}
          isCreator={false}
          isAdmin={false}
          onClose={() => setRootOpen(false)}
        />
      )}

      {/* Opened from its badge: the loop reads and edits like a node, which is
          what it is — an object on the map with its own words. */}
      {openLoop && (
        <ImpactPathNodeModal
          instanceId={instanceKey}
          isOpen
          mode="view"
          position="center"
          node={{
            id: openLoop.id,
            text: openLoop.title,
            title: openLoop.title,
            fullText: openLoop.description || '',
            description: openLoop.description,
            node_analysis: openLoop.analysis || null,
            references: openLoop.analysis?.references || [],
            is_scenario_starter: false,
          }}
          entityBadge={{
            text: loopTone(openLoop).label,
            color: loopTone(openLoop).color,
            // Which way a loop runs is a judgement about what the nodes say,
            // not about the arrows between them, so it is a person's to make.
            options: LOOP_DIRECTIONS.map((d) => ({
              ...d,
              checked: openLoop.direction === d.key,
              onSelect: () => setLoopDirection(openLoop, d.key),
            })),
            // The ring itself, so the words below have something to explain.
            ring: loopRing(openLoop, pathways),
          }}
          isCreator
          isAdmin={false}
          onClose={() => setOpenLoop(null)}
          onSave={async (data) => {
            await renameLoop(openLoop.id, data.title, data.description);
            setOpenLoop((prev) => (prev ? { ...prev, title: data.title, description: data.description } : prev));
          }}
          onDelete={async (id) => { await removeLoop(id); setOpenLoop(null); }}
        />
      )}

      {loopDirectionDraft && (
        <LoopDirectionPopover
          anchor={loopDirectionDraft.anchor}
          onSelect={async (direction) => {
            const { draft, type, closed } = loopDirectionDraft;
            setLoopDirectionDraft(null);
            const link = await createLink(draft, type);
            if (link) await createLoop(closed, draft, null, direction);
          }}
          onClose={() => setLoopDirectionDraft(null)}
        />
      )}

      {suggestionTypeEdit && (
        <EffectTypePopover
          anchor={suggestionTypeEdit.anchor}
          value={suggestionTypeEdit.suggestion.effectType}
          onSelect={(type) => {
            const target = suggestionTypeEdit.suggestion;
            setSuggestions((prev) => (prev
              ? {
                ...prev,
                items: prev.items.map((i) => (
                  i.fromId === target.fromId && i.toId === target.toId ? { ...i, effectType: type } : i
                )),
              }
              : prev));
            setSuggestionTypeEdit(null);
          }}
          onClose={() => setSuggestionTypeEdit(null)}
        />
      )}

      {proposedLinkEdit && (
        <ProposedLinkPopover
          anchor={proposedLinkEdit.anchor}
          link={{
            ...proposedLinkEdit.link,
            fromText: pathways.find((n) => n.id === proposedLinkEdit.link.from)?.text || '',
            toText: pathways.find((n) => n.id === proposedLinkEdit.link.to)?.text || '',
          }}
          onDecide={(decision) => decideProposedLink(proposedLinkEdit.link, decision)}
          onClose={() => setProposedLinkEdit(null)}
        />
      )}

      {crossTypeEdit && (
        <EffectTypePopover
          anchor={crossTypeEdit.anchor}
          value={crossTypeEdit.link.effectType || null}
          onSelect={(type) => { retypeCrossLink(crossTypeEdit.link, type); setCrossTypeEdit(null); }}
          onClose={() => setCrossTypeEdit(null)}
        />
      )}

      {editEdge && (
        <EffectTypePopover
          anchor={editEdge.anchor}
          value={pathways.find((n) => n.id === editEdge.edge?.to)?.effectType || null}
          onSelect={(type) => { retypeEdge(editEdge.edge, type); setEditEdge(null); }}
          onClose={() => setEditEdge(null)}
        />
      )}

      {createParent && !pendingEffectType && (
        <EffectTypePopover
          anchor={createAnchor}
          onSelect={setPendingEffectType}
          onClose={() => { setCreateParent(null); setCreateAnchor(null); }}
        />
      )}

      {/* Tapping a card opens it — same modal v1 uses, so a node reads and edits
          identically on both screens. Kept lean on purpose: the AI actions and
          form-check plumbing come from v1 as v2 needs them. */}
      {modalVisible && selectedNode && (() => {
        const live = pathways.find((n) => n.id === selectedNode.id) || selectedNode;
        const text = live.title || live.text || 'Untitled Node';
        return (
          <ImpactPathNodeModal
            instanceId={instanceKey}
            isOpen={modalVisible}
            valence={live.valence ?? null}
            valenceSource={live.valenceSource ?? live.valence_source ?? null}
            onValenceChange={(v) => setNodeValence(live.id, v)}
            votes={{ up: live.votesUp || 0, down: live.votesDown || 0, mine: myVote }}
            onVote={(type) => voteOnNode(live.id, type)}
            comments={nodeComments}
            onComment={(text) => addNodeComment(live.id, text)}
            proposal={proposalFor(live)}
            mode="view"
            position="center" width={wideNeighbourhood ? NEIGHBOURHOOD_MODAL_WIDTH : null}
            node={{
              id: live.id,
              text,
              title: text,
              fullText: live.description || live.human_text || text,
              description: live.description,
              human_text: live.human_text,
              ai_analysis: live.ai_analysis,
              node_analysis: live.node_analysis,
              is_scenario_starter: live.is_scenario_starter === true,
              sources: live.sources || [],
              references: live.references || live.ai_sources || [],
            }}
            isCreator={(live.participant_id || live.participantId) === participantId}
            isAdmin={false}
            hasOthersChildren={pathways.filter((n) => (n.parentId || n.parent_id) === live.id
              && n.participantId && n.participantId !== participantId && n.participantId !== 'system').length}
            onClose={() => { setModalVisible(false); setSelectedNode(null); setNodeComments([]); setMyVote(null); }}
            onDelete={async (nodeId) => {
              const response = await fetch(API_ENDPOINTS.REMOVE_NODE(), {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ instanceId: instanceKey, nodeId, participantId }),
              });
              if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.error || 'Failed to delete node');
              }
              // Drop the node and everything under it from local state; the
              // server cascades, so a refetch would show the same thing.
              const childrenBy = {};
              pathways.forEach((n) => {
                const parent = n.parentId || n.parent_id;
                if (parent) (childrenBy[parent] = childrenBy[parent] || []).push(n.id);
              });
              const doomed = new Set([nodeId]);
              const stack = [nodeId];
              while (stack.length) {
                (childrenBy[stack.pop()] || []).forEach((id) => {
                  if (!doomed.has(id)) { doomed.add(id); stack.push(id); }
                });
              }
              setPathways((prev) => prev.filter((n) => !doomed.has(n.id)));
              setModalVisible(false);
              setSelectedNode(null);
            }}
            onSave={async (data) => {
              await handleNodeUpdate({ id: data.id, title: data.title, description: data.description });
              setSelectedNode((prev) => (prev ? { ...prev, title: data.title, text: data.title, description: data.description } : prev));
            }}
          />
        );
      })()}
      {modalVisible && selectedNode && wideNeighbourhood && <NodeNeighbourhood instanceId={instanceKey} nodeId={selectedNode.id}
        onOpen={(id) => { const next = pathways.find((n) => n.id === id); if (next) { setNodeComments([]); setSelectedNode(next); } }} />}

      {/* After the type is picked, the consequence is written in the one
          proven dialog (CreateNodeModal, with valence and an optional title
          for v2) and goes on the map at once. Then, with a loader on
          the card: the AI condenses a title if none was given, and the
          description is matched against the map; similar nodes are offered
          at the bottom (§4f), never before the node exists. */}
      {createParent && pendingEffectType && (
        <CreateNodeModal
          visible
          parentNode={createParent}
          showEffectType
          initialEffectType={pendingEffectType}
          showValence
          showKind={sceneMap}
          titleOptional
          title={createParent.valence === 'unwanted' ? t('impactPath.addNode.titleInstead') : t('impactPath.addNode.title')}
          subtitle={t('impactPath.addNode.subtitle')}
          saveLabel={t('impactPath.addNode.save')}
          onSave={async (data) => {
            const parent = createParent;
            setCreateParent(null);
            setPendingEffectType(null);
            setCreateAnchor(null);
            const provisional = data.text || provisionalTitle(data.description);
            const description = data.description || data.text;
            const newNodeId = await handleSaveConsequence(parent, {
              title: provisional,
              description,
              effectType: data.effectType || pendingEffectType,
              valence: data.valence || null,
              ai_sources: [],
              // What the card is in the chain (condition / outcome / output), when chosen:
              // the map graph reads node_analysis.chainKind, and the AI's analysis merges
              // into node_analysis rather than replacing it.
              node_analysis: data.kind ? { chainKind: data.kind } : null,
            });
            if (!newNodeId) return;
            setBrewingIds((prev) => new Set(prev).add(newNodeId));
            let title = provisional;
            try {
              if (!data.text) {
                const r = await fetch(API_ENDPOINTS.AI_GENERATE_TITLE(), {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ fullText: description, nodeType: 'consequence', instanceId: instanceKey }),
                });
                const j = r.ok ? await r.json() : null;
                if (j?.title) {
                  title = j.title;
                  await fetch(API_ENDPOINTS.UPDATE_PATHWAY(newNodeId), {
                    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title }),
                  });
                  setPathways((prev) => prev.map((n) => (n.id === newNodeId ? { ...n, title, text: title } : n)));
                }
              }
              await findSimilar(newNodeId, title, parent, description);
            } catch (err) {
              console.warn('[impact-map] after-add work failed:', err.message);
            } finally {
              setBrewingIds((prev) => { const next = new Set(prev); next.delete(newNodeId); return next; });
              setJustAdded({ id: newNodeId, title });
            }
          }}
          onClose={() => { setCreateParent(null); setPendingEffectType(null); setCreateAnchor(null); }}
        />
      )}

      {/* "Is it the same as one of these?" — asked after the node exists. A
          pick draws an *on sama* link between the two; nothing is deleted. */}
      {findingsOpen && (
        <SimilarAfterAddModal
          findings={findings}
          onSame={async (node) => {
            const link = await createLink(
              { fromId: findings.nodeId, fromKind: 'node', toId: node.id, toKind: 'node' }, 'is_same',
            );
            setFindingsOpen(false);
            clearFindings();
            if (link) await loadPathways();
          }}
          onClose={() => { setFindingsOpen(false); clearFindings(); }}
        />
      )}

      <SettingsDrawer
        visible={settingsVisible}
        onClose={() => setSettingsVisible(false)}
        settings={[]}
      />
    </Container>
  );
});
